import { ChevronDown } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import TestFooter from '../components/Features/PracticeTest/TestFooter';
import TestHeader from '../components/Features/PracticeTest/TestHeader';
import { setIsStartTest } from '../redux/Slices/QuestionsSlice';
import {
  setCurrentCodeingQuestionAnswer,
  setCurrentQuestionIndex,
  setCurrentTime,
  setShowInstructions
} from '../redux/Slices/StartTestSlice/startFulTest';
import { useAppDispatch, useAppSelector } from '../redux/store/hooks';

import EditorPage from './EditorPage';
import { QuestionData } from './ProgrammingTest';

// Mock data for topics (using the provided JSON structure)
const topics = [
  {
    id: '67f4f0a812b8026f68aca590',
    displayOnWebsite: 'General Coding'
  },
  {
    id: '681c8a23f942c2bf6868ea59',
    displayOnWebsite: 'Programming Languages'
  },
  {
    id: '68b992743c03d50a6e000647',
    displayOnWebsite: 'Java Programming'
  },
  {
    id: '68b9988d3c03d50a6e000650',
    displayOnWebsite: 'C Programming'
  },
  {
    id: '68b999f03c03d50a6e000652',
    displayOnWebsite: 'C++ Programming'
  },
  {
    id: '68b9ac5b3c03d50a6e00065c',
    displayOnWebsite: 'Tries'
  },
  {
    id: '68c15127e1e897cd95af907f',
    displayOnWebsite: 'Hibernate'
  },
  {
    id: '68ca95b715aa537a965f682e',
    displayOnWebsite: 'Data Structures-Programming'
  },
  {
    id: '68ca9bef15aa537a965f6832',
    displayOnWebsite: 'Artificial Intelligence - Programming'
  },
  {
    id: '68ca9e8015aa537a965f6834',
    displayOnWebsite: 'Array Programs'
  },
  {
    id: '68ca9f2115aa537a965f6835',
    displayOnWebsite: 'Hashing Programs'
  },
  {
    id: '68ca9f9c15aa537a965f6836',
    displayOnWebsite: 'Binary Search Tree Programs'
  },
  {
    id: '68caa44415aa537a965f6837',
    displayOnWebsite: 'Control flow statements Programs'
  },
  {
    id: '68caa58015aa537a965f6839',
    displayOnWebsite: 'Python Programs'
  },
  {
    id: '68caa61815aa537a965f683a',
    displayOnWebsite: 'Datatypes Programs'
  },
  {
    id: '68caa92815aa537a965f683b',
    displayOnWebsite: 'Algorithm Programs'
  },
  {
    id: '68caad6015aa537a965f683d',
    displayOnWebsite: 'Tree Programs'
  },
  {
    id: '68cab26015aa537a965f683e',
    displayOnWebsite: 'Collections Programs'
  },
  {
    id: '68cab37515aa537a965f683f',
    displayOnWebsite: 'File Handling Programs'
  },
  {
    id: '68cab6ab15aa537a965f6841',
    displayOnWebsite: 'SQL Programs'
  },
  {
    id: '68cabe5915aa537a965f6844',
    displayOnWebsite: 'Sorting Programs'
  },
  {
    id: '68cac4f315aa537a965f6846',
    displayOnWebsite: 'C sharp Programs'
  },
  {
    id: '68cac9d715aa537a965f6847',
    displayOnWebsite: 'Stack Programs'
  },
  {
    id: '68cad62f15aa537a965f684d',
    displayOnWebsite: 'Data Structures Programs'
  },
  {
    id: '68cad7e615aa537a965f684f',
    displayOnWebsite: 'Operators - Programs'
  },
  {
    id: '68cb9cf815aa537a965f6853',
    displayOnWebsite: 'Linked List Programs'
  },
  {
    id: '68cba12b15aa537a965f6854',
    displayOnWebsite: 'OOP Concepts Programs'
  },
  {
    id: '68cbd1f015aa537a965f6858',
    displayOnWebsite: 'Web Technologies Programs'
  },
  {
    id: '68cbd5be15aa537a965f685b',
    displayOnWebsite: 'Miscellaneous Programs'
  }
];

// Comprehensive test data similar to LeetCode for all topics
const getTestsForTopic = (topicId: string) => {
  const testSets: Record<string, any[]> = {
    '67f4f0a812b8026f68aca590': [
      // General Coding
      {
        id: 1,
        title: 'FizzBuzz',
        description: 'Generate all prime numbers up to a given number n',
        difficulty: 'Easy',
        category: 'General'
      },
      {
        id: 2,
        title: 'Palindrome Check',
        description: 'Determine if a given string is a palindrome',
        difficulty: 'Easy',
        category: 'General'
      },
      {
        id: 3,
        title: 'Prime Number Generator',
        description: 'Generate all prime numbers up to a given number n',
        difficulty: 'Medium',
        category: 'General'
      }
    ],
    '681c8a23f942c2bf6868ea59': [
      // Programming Languages
      {
        id: 1,
        title: 'Language Syntax Quiz',
        description:
          'Test your knowledge of different programming language syntax',
        difficulty: 'Easy',
        category: 'Syntax'
      },
      {
        id: 2,
        title: 'OOP Principles',
        description:
          'Implement inheritance, polymorphism, and encapsulation concepts',
        difficulty: 'Medium',
        category: 'OOP'
      }
    ],
    '68b992743c03d50a6e000647': [
      // Java Programming
      {
        id: 1,
        title: 'ArrayList vs LinkedList',
        description: 'Compare performance and implement custom operations',
        difficulty: 'Medium',
        category: 'Collections'
      },
      {
        id: 2,
        title: 'Exception Handling',
        description:
          'Create custom exceptions and handle multiple exception types',
        difficulty: 'Easy',
        category: 'Exception'
      },
      {
        id: 3,
        title: 'Multithreading Synchronization',
        description: 'Implement thread-safe operations using synchronization',
        difficulty: 'Hard',
        category: 'Concurrency'
      }
    ],
    '68b9988d3c03d50a6e000650': [
      // C Programming
      {
        id: 1,
        title: 'Pointer Manipulation',
        description:
          'Work with pointers, pointer arithmetic, and memory allocation',
        difficulty: 'Medium',
        category: 'Pointers'
      },
      {
        id: 2,
        title: 'Dynamic Memory Management',
        description: 'Implement malloc, calloc, realloc, and free operations',
        difficulty: 'Hard',
        category: 'Memory'
      },
      {
        id: 3,
        title: 'File I/O Operations',
        description:
          'Read, write, and manipulate files using C standard library',
        difficulty: 'Easy',
        category: 'File I/O'
      }
    ],
    '68b999f03c03d50a6e000652': [
      // C++ Programming
      {
        id: 1,
        title: 'STL Containers',
        description:
          'Implement operations using vector, map, set, and other STL containers',
        difficulty: 'Medium',
        category: 'STL'
      },
      {
        id: 2,
        title: 'Template Programming',
        description: 'Create generic functions and classes using templates',
        difficulty: 'Hard',
        category: 'Templates'
      },
      {
        id: 3,
        title: 'RAII and Smart Pointers',
        description: 'Implement resource management using RAII principles',
        difficulty: 'Medium',
        category: 'Memory Management'
      }
    ],
    '68ca9e8015aa537a965f6834': [
      // Array Programs
      {
        id: 1,
        title: 'Two Sum',
        description: 'Find two numbers in array that add up to target sum',
        difficulty: 'Easy',
        category: 'Arrays'
      },
      {
        id: 2,
        title: 'Rotate Array',
        description: 'Rotate array to the right by k steps',
        difficulty: 'Medium',
        category: 'Arrays'
      },
      {
        id: 3,
        title: 'Remove Duplicates',
        description: 'Remove duplicates from sorted array in-place',
        difficulty: 'Easy',
        category: 'Arrays'
      },
      {
        id: 4,
        title: 'Maximum Subarray',
        description: 'Remove duplicates from sorted array in-place',
        difficulty: 'Medium',
        category: 'Arrays'
      },
      {
        id: 5,
        title: 'Merge Intervals',
        description: 'Merge overlapping intervals in array',
        difficulty: 'Medium',
        category: 'Arrays'
      },
      {
        id: 6,
        title: 'Product of Array Except Self',
        description:
          'Return array where each element is product of all other elements',
        difficulty: 'Medium',
        category: 'Arrays'
      }
    ],
    '68ca9f2115aa537a965f6835': [
      // Hashing Programs
      {
        id: 1,
        title: 'Valid Anagram',
        description: 'Check if two strings are anagrams using hash table',
        difficulty: 'Easy',
        category: 'Hashing'
      },
      {
        id: 2,
        title: 'Group Anagrams',
        description: 'Group strings that are anagrams of each other',
        difficulty: 'Medium',
        category: 'Hashing'
      },
      {
        id: 3,
        title: 'Longest Substring Without Repeating',
        description:
          'Find length of longest substring without repeating characters',
        difficulty: 'Medium',
        category: 'Hashing'
      },
      {
        id: 4,
        title: 'Design HashMap',
        description:
          'Implement a hash map with put, get, and remove operations',
        difficulty: 'Easy',
        category: 'Design'
      }
    ],
    '68ca9f9c15aa537a965f6836': [
      // Binary Search Tree Programs
      {
        id: 1,
        title: 'Validate BST',
        description: 'Determine if a binary tree is a valid binary search tree',
        difficulty: 'Medium',
        category: 'BST'
      },
      {
        id: 2,
        title: 'Lowest Common Ancestor',
        description: 'Find lowest common ancestor of two nodes in BST',
        difficulty: 'Easy',
        category: 'BST'
      },
      {
        id: 3,
        title: 'Convert Sorted Array to BST',
        description: 'Convert sorted array to height-balanced BST',
        difficulty: 'Easy',
        category: 'BST'
      },
      {
        id: 4,
        title: 'Delete Node in BST',
        description: 'Delete a node from BST while maintaining BST properties',
        difficulty: 'Medium',
        category: 'BST'
      }
    ],
    '68caa44415aa537a965f6837': [
      // Control Flow Statements
      {
        id: 1,
        title: 'Nested Loop Patterns',
        description: 'Print various patterns using nested loops',
        difficulty: 'Easy',
        category: 'Loops'
      },
      {
        id: 2,
        title: 'Switch Case Calculator',
        description: 'Implement calculator using switch-case statements',
        difficulty: 'Easy',
        category: 'Switch'
      },
      {
        id: 3,
        title: 'Break and Continue Logic',
        description: 'Solve problems using break and continue statements',
        difficulty: 'Medium',
        category: 'Control'
      }
    ],
    '68caa58015aa537a965f6839': [
      // Python Programs
      {
        id: 1,
        title: 'List Comprehensions',
        description:
          'Master Python list comprehensions with complex conditions',
        difficulty: 'Medium',
        category: 'Python'
      },
      {
        id: 2,
        title: 'Decorators',
        description:
          'Implement and use Python decorators for function enhancement',
        difficulty: 'Hard',
        category: 'Python'
      },
      {
        id: 3,
        title: 'Generator Functions',
        description: 'Create memory-efficient generators for large datasets',
        difficulty: 'Medium',
        category: 'Python'
      },
      {
        id: 4,
        title: 'Context Managers',
        description:
          'Implement custom context managers using __enter__ and __exit__',
        difficulty: 'Medium',
        category: 'Python'
      }
    ],
    '68caa92815aa537a965f683b': [
      // Algorithm Programs
      {
        id: 1,
        title: 'Binary Search',
        description: 'Implement binary search and its variations',
        difficulty: 'Easy',
        category: 'Search'
      },
      {
        id: 2,
        title: 'Merge Sort',
        description: 'Implement merge sort algorithm',
        difficulty: 'Medium',
        category: 'Sorting'
      },
      {
        id: 3,
        title: 'Quick Sort',
        description: 'Implement quick sort with random pivot',
        difficulty: 'Medium',
        category: 'Sorting'
      },
      {
        id: 4,
        title: 'Dijkstra Algorithm',
        description: 'Find shortest path in weighted graph',
        difficulty: 'Hard',
        category: 'Graph'
      },
      {
        id: 5,
        title: 'Dynamic Programming - Fibonacci',
        description: 'Optimize Fibonacci using memoization and tabulation',
        difficulty: 'Medium',
        category: 'DP'
      }
    ],
    '68caad6015aa537a965f683d': [
      // Tree Programs
      {
        id: 1,
        title: 'Binary Tree Traversal',
        description: 'Implement inorder, preorder, and postorder traversals',
        difficulty: 'Easy',
        category: 'Tree'
      },
      {
        id: 2,
        title: 'Maximum Depth of Binary Tree',
        description: 'Find the maximum depth of a binary tree',
        difficulty: 'Easy',
        category: 'Tree'
      },
      {
        id: 3,
        title: 'Level Order Traversal',
        description: 'Traverse tree level by level (BFS)',
        difficulty: 'Medium',
        category: 'Tree'
      },
      {
        id: 4,
        title: 'Serialize and Deserialize Tree',
        description: 'Convert tree to string and back to tree',
        difficulty: 'Hard',
        category: 'Tree'
      }
    ],
    '68cb9cf815aa537a965f6853': [
      // Linked List Programs
      {
        id: 1,
        title: 'Reverse Linked List',
        description: 'Reverse a singly linked list iteratively and recursively',
        difficulty: 'Easy',
        category: 'Linked List'
      },
      {
        id: 2,
        title: 'Merge Two Sorted Lists',
        description: 'Merge two sorted linked lists into one sorted list',
        difficulty: 'Easy',
        category: 'Linked List'
      },
      {
        id: 3,
        title: 'Detect Cycle',
        description: 'Detect if linked list has a cycle using Floyd algorithm',
        difficulty: 'Easy',
        category: 'Linked List'
      },
      {
        id: 4,
        title: 'Remove Nth Node from End',
        description: 'Remove the nth node from the end of linked list',
        difficulty: 'Medium',
        category: 'Linked List'
      },
      {
        id: 5,
        title: 'Add Two Numbers',
        description: 'Add two numbers represented as linked lists',
        difficulty: 'Medium',
        category: 'Linked List'
      }
    ],
    '68cac9d715aa537a965f6847': [
      // Stack Programs
      {
        id: 1,
        title: 'Valid Parentheses',
        description: 'Check if string of brackets is properly balanced',
        difficulty: 'Easy',
        category: 'Stack'
      },
      {
        id: 2,
        title: 'Evaluate Reverse Polish Notation',
        description: 'Evaluate arithmetic expression in postfix notation',
        difficulty: 'Medium',
        category: 'Stack'
      },
      {
        id: 3,
        title: 'Min Stack',
        description:
          'Design stack that supports push, pop, top, and getMin in O(1)',
        difficulty: 'Medium',
        category: 'Stack'
      },
      {
        id: 4,
        title: 'Largest Rectangle in Histogram',
        description: 'Find area of largest rectangle in histogram',
        difficulty: 'Hard',
        category: 'Stack'
      }
    ],
    '68cad62f15aa537a965f684d': [
      // Data Structures Programs
      {
        id: 1,
        title: 'Implement Queue using Stacks',
        description: 'Design queue using only stack operations',
        difficulty: 'Easy',
        category: 'Design'
      },
      {
        id: 2,
        title: 'LRU Cache',
        description: 'Design and implement Least Recently Used cache',
        difficulty: 'Medium',
        category: 'Design'
      },
      {
        id: 3,
        title: 'Trie Implementation',
        description: 'Implement prefix tree (trie) for word search',
        difficulty: 'Medium',
        category: 'Trie'
      },
      {
        id: 4,
        title: 'Design HashSet',
        description: 'Design a HashSet without using built-in libraries',
        difficulty: 'Easy',
        category: 'Design'
      }
    ],
    '68cabe5915aa537a965f6844': [
      // Sorting Programs
      {
        id: 1,
        title: 'Bubble Sort Implementation',
        description: 'Implement bubble sort with optimization',
        difficulty: 'Easy',
        category: 'Sorting'
      },
      {
        id: 2,
        title: 'Heap Sort',
        description: 'Implement heap sort algorithm',
        difficulty: 'Medium',
        category: 'Sorting'
      },
      {
        id: 3,
        title: 'Counting Sort',
        description: 'Implement counting sort for integer arrays',
        difficulty: 'Easy',
        category: 'Sorting'
      },
      {
        id: 4,
        title: 'Kth Largest Element',
        description: 'Find kth largest element using quickselect',
        difficulty: 'Medium',
        category: 'Sorting'
      }
    ],
    '68b9ac5b3c03d50a6e00065c': [
      // Tries
      {
        id: 1,
        title: 'Word Search II',
        description: 'Find all words from dictionary in 2D board using trie',
        difficulty: 'Hard',
        category: 'Trie'
      },
      {
        id: 2,
        title: 'Auto-complete System',
        description: 'Design auto-complete system using trie',
        difficulty: 'Hard',
        category: 'Trie'
      },
      {
        id: 3,
        title: 'Replace Words',
        description: 'Replace words with their roots using trie',
        difficulty: 'Medium',
        category: 'Trie'
      }
    ],
    '68caa61815aa537a965f683a': [
      // Datatypes Programs
      {
        id: 1,
        title: 'Type Conversion Challenges',
        description: 'Handle various data type conversions safely',
        difficulty: 'Easy',
        category: 'Types'
      },
      {
        id: 2,
        title: 'Custom Data Type Design',
        description: 'Design custom data types with proper validation',
        difficulty: 'Medium',
        category: 'Types'
      },
      {
        id: 3,
        title: 'Generic Type Implementation',
        description:
          'Implement generic types that work with multiple data types',
        difficulty: 'Hard',
        category: 'Types'
      }
    ]
  };

  return testSets[topicId] || [];
};

const ProgrammingTestV2: React.FC = () => {
  const [selectedTopic, setSelectedTopic] = useState<string>('');
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [tests, setTests] = useState<any[]>([]);
  const dispatch = useAppDispatch();
  const [questionData, setQuestionData] = useState<QuestionData>(
    {} as QuestionData
  );

  const { written, isStartTest } = useAppSelector(
    state => state.root.questions
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const {
    roundName,
    currentQuestionIndex,
    currentSectionName,
    allCodeingQuestions,
    isResultsScreen
  } = useAppSelector(state => state.root.startFulltest);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const handleNext = () => {
    if (currentQuestionIndex < written.length - 1) {
      dispatch(setCurrentCodeingQuestionAnswer(''));
      dispatch(setCurrentQuestionIndex(currentQuestionIndex + 1));
    }
  };

  const handleBack = () => {
    if (currentQuestionIndex > 0) {
      dispatch(setCurrentQuestionIndex(currentQuestionIndex - 1));
    }
  };

  const hadleQuestionJump = (number: number) => {
    dispatch(setCurrentQuestionIndex(number));
  };

  const handleSubmit = () => {
    setIsSubmitting(true);
    dispatch(setIsStartTest(false));
  };

  useEffect(() => {
    dispatch(setCurrentTime(1800));
  }, [currentQuestionIndex]);

  const handleStartTest = async () => {
    try {
      dispatch(setShowInstructions(false));
      setQuestionData({
        id: '68d7b7ada7daff07e531dada',
        isActive: true,
        userId: '68c423b73b626a728c937a9c',
        createdDate: '2025-09-27T10:08:45.187142155',
        updatedDate: '2025-09-27T10:08:45.187146369',
        question: {
          id: '68947236a3948d136262802e',
          isActive: true,
          createdDate: '2025-08-07T09:30:30.118',
          questionTypeId: '67f799d4a46b8d137fcbaecf',
          roundId: '67f4da5312b8026f68aca585',
          sectionId: '67f4dcf912b8026f68aca589',
          topicId: '67f4f0a812b8026f68aca590',
          subTopic: 'Programming',
          questionText:
            'Write a program to calculate the area and perimeter of a rectangle, using user inputs for length and breadth.',
          explanation: null,
          isProgrammingQuestion: true,
          options: null,
          sourceNameId: '68b194bb863fadd0fa9aa94e',
          correctAnswer: null,
          questionStatus: 4003
        },
        answers: null,
        testStatus: 'STARTED'
      });
      dispatch(setIsStartTest(true));
    } catch (error) {
      console.error('Error in handleStartTest:', error);
      toast.error('Error starting test');
    }
  };

  const handleTopicSelect = (topicId: string) => {
    setSelectedTopic(topicId);
    setIsDropdownOpen(false);
    const topicTests = getTestsForTopic(topicId);
    setTests(topicTests);
  };

  const selectedTopicData = topics.find(topic => topic.id === selectedTopic);
  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty.toLowerCase()) {
      case 'easy':
        return 'text-green-600';
      case 'medium':
        return 'text-orange-600';
      case 'hard':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  return (
    <>
      {isStartTest && questionData?.id ? (
        <div className='mt-[60px] flex h-full flex-col overflow-hidden overscroll-contain bg-white md:mt-[80px]'>
          <TestHeader
            title={'Coding Assessment'}
            time='0:00'
            currentSectionName={currentSectionName}
            shouldShowTimer={false}
            closeFunction={() => {
              console.log('clicked');
            }}
          />
          <div className='text-s mx-[45px] flex h-[35px] items-center justify-center rounded-b-2xl bg-primary py-2 text-center font-semibold text-white'>
            {roundName}
          </div>
          <div
            style={{ height: 'calc(100vh - 250px)' }}
            className='flex w-full overflow-auto'
          >
            <div className='mb-8 mt-2 flex h-full w-full justify-center'>
              <div className='flex w-full flex-col gap-4 p-2 md:flex-row md:p-4'>
                <div
                  className='w-full rounded-lg bg-white p-3 shadow-sm md:w-[30%] md:p-6'
                  dangerouslySetInnerHTML={{
                    __html: questionData?.question?.questionText ?? ''
                  }}
                />
                <div className='w-full'>
                  <EditorPage
                    questionNumber={1}
                    questionText={questionData?.question?.questionText}
                    questionId={questionData?.question?.id}
                    sectionId={questionData?.question?.sectionId}
                  />
                </div>
              </div>
            </div>
          </div>
          <TestFooter
            // onBack={handleBack}
            // onNext={handleNext}
            // onSubmit={handleSubmit}
            questionNumber={0}
            // disableBack={true}
            // disableNext={true}
            isProgrammingQuestion={true}
            hadleQuestionJump={hadleQuestionJump}
            isProgrammingTest={true}
            isLoading={isSubmitting}
            shouldShowTimer={false}
            examName='Programming Language'
          />
        </div>
      ) : (
        <div className='min-h-screen bg-gray-50 p-6'>
          <div className='mx-auto max-w-4xl'>
            {/* Topic Selection */}
            <div className='mb-6 rounded-lg border border-gray-200 bg-white p-6 shadow-sm'>
              <h2 className='mb-4 text-xl font-semibold text-gray-900'>
                Select Programming Topic
              </h2>

              <div className='relative'>
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className='flex w-full items-center justify-between rounded-lg border border-gray-300 bg-white px-4 py-3 shadow-sm transition-colors hover:border-gray-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 md:w-96'
                >
                  <span
                    className={
                      selectedTopicData ? 'text-gray-900' : 'text-gray-500'
                    }
                  >
                    {selectedTopicData
                      ? selectedTopicData.displayOnWebsite
                      : 'Choose a topic...'}
                  </span>
                  <ChevronDown
                    className={`h-5 w-5 text-gray-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
                  />
                </button>

                {isDropdownOpen && (
                  <div className='absolute z-10 mt-2 max-h-60 w-full overflow-y-auto rounded-lg border border-gray-300 bg-white shadow-lg md:w-96'>
                    {topics.map(topic => (
                      <button
                        key={topic.id}
                        onClick={() => handleTopicSelect(topic.id)}
                        className='w-full border-b border-gray-100 px-4 py-3 text-left transition-colors last:border-b-0 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none'
                      >
                        <span className='text-gray-900'>
                          {topic.displayOnWebsite}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Test Results */}
            {selectedTopic && (
              <div className='rounded-lg border border-gray-200 bg-white p-6 shadow-sm'>
                <div className='mb-6 flex items-center justify-between'>
                  <div>
                    <h1 className='text-2xl font-semibold text-gray-900'>
                      Coding Assessment: {selectedTopicData?.displayOnWebsite}{' '}
                      (Medium)
                    </h1>
                    <p className='mt-1 text-gray-600'>
                      {tests.length} questions available
                    </p>
                  </div>
                </div>

                {tests.length > 0 ? (
                  <div className='space-y-4'>
                    {tests.map(test => (
                      <div
                        onClick={handleStartTest}
                        key={test.id}
                        className='cursor-pointer rounded-lg border border-gray-200 p-6 transition-shadow hover:shadow-md'
                      >
                        <div className='flex items-center justify-between'>
                          <div className='flex-1'>
                            <h3 className='mb-2 text-lg font-medium text-gray-900'>
                              {test.title}
                            </h3>
                            <p className='mb-2 text-gray-600'>
                              {test.description}
                            </p>
                          </div>
                          <div className='text-right'>
                            <span
                              className={`text-sm font-medium ${getDifficultyColor(test.difficulty)}`}
                            >
                              {test.difficulty}
                            </span>
                            <p className='mt-1 text-sm text-gray-500'>
                              {test.category}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className='py-12 text-center'>
                    <div className='mb-4 text-gray-400'>
                      <svg
                        className='mx-auto h-16 w-16'
                        fill='none'
                        stroke='currentColor'
                        viewBox='0 0 24 24'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={1.5}
                          d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
                        />
                      </svg>
                    </div>
                    <h3 className='mb-2 text-lg font-medium text-gray-900'>
                      No tests available
                    </h3>
                    <p className='text-gray-600'>
                      There are currently no programming tests available for
                      this topic.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Initial State */}
            {!selectedTopic && (
              <div className='rounded-lg border border-gray-200 bg-white p-12 shadow-sm'>
                <div className='text-center'>
                  <div className='mb-4 text-gray-400'>
                    <svg
                      className='mx-auto h-16 w-16'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={1.5}
                        d='M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4'
                      />
                    </svg>
                  </div>
                  <h3 className='mb-2 text-lg font-medium text-gray-900'>
                    Select a Programming Topic
                  </h3>
                  <p className='text-gray-600'>
                    Choose a topic from the dropdown above to view available
                    programming tests.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ProgrammingTestV2;
